import { CdkStepperModule } from '@angular/cdk/stepper';
import { NgForOf, NgIf } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Auth } from '@angular/fire/auth';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { Store } from '@ngrx/store';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import AwsS3 from '@uppy/aws-s3';
import { Uppy } from '@uppy/core';
import axios from 'axios';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs/internal/Subscription';
import { environment } from '../../../../../environments/environment';
import { Channel } from '../../../../models/channel.model';
import { Video } from '../../../../models/video.model';
import { AuthService } from '../../../../services/auth.service';
import { ChannelService } from '../../../../services/channel.service';
import { VideoService } from '../../../../services/video.service';
import * as videoActions from '../../../../state/actions/video.actions';
import { AppState } from '../../../../state/appstate';
import { LoadingDotsComponent } from '../../../components/spinners/loading-dots/loading-dots.component';

@Component({
  selector: 'app-create-video',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    ReactiveFormsModule,
    LoadingDotsComponent,
    CdkStepperModule,
    MatStepperModule,
    UppyAngularDashboardModule,
    UppyAngularDashboardModalModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
  ],
  templateUrl: './create-video.component.html',
  styleUrls: ['./create-video.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class CreateVideoComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') private stepper!: MatStepper;

  pageForm: FormGroup;
  tags: Array<string> = [];

  uppy: Uppy = new Uppy({
    restrictions: {
      maxFileSize: 5368709120, // 5 GB
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.flv'],
    },
  });
  dashboardProps = {
    inline: true,
    proudlyDisplayPoweredByUppy: false,
    height: '300px',
    width: '100%',
    note: 'Video file only (Max size: 5GB). Allowed formats: mp4, mov, avi, mkv, webm, flv',
  };

  channel$: Subscription | undefined;
  channel: Channel | null = null;

  videoState$: Subscription | undefined;

  working: boolean = false;
  fileUploaded: boolean = false;

  progress: number = 0;
  message: string = '';

  constructor(
    private fb: FormBuilder,
    private store: Store<AppState>,
    private channelService: ChannelService,
    private videoService: VideoService,
    private auth: AuthService,
    private fbAuth: Auth,
    private toastr: ToastrService
  ) {
    this.pageForm = this.fb.group({
      videoFileKey: ['', Validators.required],
      title: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(200),
      ]),
      description: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(500),
      ]),
      tagField: new FormControl(''),
    });

    this.channel$ = this.store
      .select((state) => state.channel.channel)
      .subscribe((channel) => {
        this.channel = channel;
      });
    this.videoState$ = this.store
      .select((state) => state.videos)
      .subscribe((videos) => {
        this.working = videos.inProgress;
      });

    this.uppy.use(AwsS3, {
      async getUploadParameters(file) {
        // console.log(`Getting upload parameters for file: ${file.name}`);
        const fileExtn: string | undefined = file.name?.split('.')?.pop();
        const token: string | undefined =
          await fbAuth.currentUser?.getIdToken();
        // console.log(`File Type: ${file.type}, File Extn: ${fileExtn}`);
        return await axios
          .get<any>(environment['AWS_VIDEO_SIGNEDURL']!, {
            params: { id: file.name, fileExtn: fileExtn },
            headers: {
              'x-api-key': environment['AWS_API_KEY']!,
              AuthorizationToken: `Bearer ${token}`,
              'Content-Type': `video/${fileExtn}`,
            },
          })
          .then((response) => {
            return {
              url: response.data.url,
              headers: {
                'Content-Type': `video/${fileExtn}`,
              },
            };
          })
          .catch((error) => {
            console.error(`Error getting upload parameters: ${error}`);
            throw error;
          });
      },
      shouldUseMultipart: () => true,
      async createMultipartUpload(file) {
        // console.log(`Creating multi-part upload for file: ${file.name}`);
        const token: string | undefined =
          await fbAuth.currentUser?.getIdToken();
        // console.log(`Token: ${token}`);
        return await axios
          .get(environment['AWS_VIDEO_UPLOAD_INITIALIZE']!, {
            params: { key: file.name, ContentType: file.type },
            headers: {
              'x-api-key': environment['AWS_API_KEY']!,
              // 'Access-Control-Allow-Origin': '*',
              // 'Access-Control-Allow-Methods': 'GET,PUT,POST',
              // 'Access-Control-Allow-Credentials': 'true',
              // 'Access-Control-Request-Headers':
              //   'X-Requested-With, Content-Type, AuthorizationToken',
              AuthorizationToken: `Bearer ${token}`,
            },
          })
          .then((response) => response.data)
          .catch((error) => {
            // console.error(`Error creating multi-part upload: ${error}`);
            throw error;
          });
      },
      async listParts(file, { uploadId, key }) {
        // console.log(`Listing parts for file: ${file.name}`);
        const token: string | undefined =
          await fbAuth.currentUser?.getIdToken();
        // console.log(`Token: ${token}`);
        return axios
          .get<any>(environment['AWS_VIDEO_UPLOAD_LIST_PARTS']!, {
            params: { uploadId: uploadId, key: key },
            headers: {
              'x-api-key': environment['AWS_API_KEY']!,
              AuthorizationToken: `Bearer ${token}`,
            },
          })
          .then((response) => response.data)
          .catch((error) => {
            // console.error(`Error listing parts: ${error}`);
          });
      },
      async signPart(file, partData) {
        // console.log(`Signing part for file: ${file.name}`);
        const token: string | undefined =
          await fbAuth.currentUser?.getIdToken();
        // console.log(`Token: ${token}`);
        return axios
          .get<any>(environment['AWS_VIDEO_UPLOAD_PART']!, {
            params: {
              uploadId: partData.uploadId,
              key: partData.key,
              partNumber: partData.partNumber,
            },
            headers: {
              'x-api-key': environment['AWS_API_KEY']!,
              AuthorizationToken: `Bearer ${token}`,
            },
          })
          .then((response) => {
            // console.log(`SignPart Response: ${JSON.stringify(response.data)}`);
            return { url: response.data.url };
          })
          .catch((error) => {
            // console.error(`Error signing part: ${error}`);
            throw error;
          });
      },
      async abortMultipartUpload(file, { uploadId, key }) {
        // console.log(`Aborting multi-part upload for file: ${file.name}`);
        const token: string | undefined =
          await fbAuth.currentUser?.getIdToken();
        // console.log(`Token: ${token}`);
        return await axios
          .delete<any>(environment['AWS_VIDEO_UPLOAD_ABORT']!, {
            params: { uploadId: uploadId, key: key },
            headers: {
              'x-api-key': environment['AWS_API_KEY']!,
              AuthorizationToken: `Bearer ${token}`,
            },
          })
          .then((response) => response.data)
          .catch((error) => {
            console.error(`Error aborting multi-part upload: ${error}`);
            throw error;
          });
      },
      async completeMultipartUpload(file, { uploadId, key, parts }) {
        // console.log(`Completing multi-part upload for file: ${file.name}`);
        const token: string | undefined =
          await fbAuth.currentUser?.getIdToken();
        return await axios
          .put(
            environment['AWS_VIDEO_UPLOAD_COMPLETE']!,
            {
              uploadId: uploadId,
              key: key,
              parts: parts,
            },
            {
              headers: {
                'x-api-key': environment['AWS_API_KEY']!,
                AuthorizationToken: `Bearer ${token}`,
              },
            }
          )
          .then((response) => response.data)
          .catch((error) => {
            // console.error(`Error completing multi-part upload: ${error}`);
            throw error;
          });
      },
    });

    this.uppy.on('upload-success', (file, response) => {
      // console.log(`Upload success: ${file?.name}`);
      this.fileUploaded = true;
      this.pageForm.get('videoFileKey')?.setValue(file?.name);
      if (this.stepper.selected != undefined) {
        this.stepper.selected.completed = true;
        this.stepper.selected.editable = false;
      }
      this.stepper.next();
    });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.channel$?.unsubscribe();
    this.videoState$?.unsubscribe();
  }

  addTag() {
    this.pageForm.get('tagField')?.setErrors(null);
    if (this.pageForm.get('tagField')?.value === null) return;
    const fieldValue: string = this.pageForm.get('tagField')?.value.trim();
    if (fieldValue === '') return;
    if (fieldValue.includes(' '))
      return this.pageForm.controls['tagField'].setErrors({
        invalidValue: true,
      });
    if ((this.tags.length ?? 0) >= 10)
      return this.pageForm.controls['tagField'].setErrors({
        maxValuesAllowed: true,
      });
    this.tags = [...this.tags, fieldValue];
    this.pageForm.get('tagField')?.setValue('');
    this.pageForm.get('tagField')?.markAsPristine();
  }

  removeTag(tag: number) {
    this.tags.splice(tag, 1);
  }

  createVideo(): void {
    try {
      this.store.dispatch(
        videoActions.create({
          video: {
            title: this.pageForm.controls['title'].value,
            description: this.pageForm.controls['description'].value,
            tags: this.tags,
            link: this.pageForm.controls['videoFileKey'].value,
            channelName: this.channel?.name!,
            channelId: this.channel?.id!,
            channelImageUrl: this.channel?.imageUrl!,
            createdBy: this.channel?.createdBy!,
          } as Partial<Video>,
        })
      );
    } catch (error) {
      this.toastr.error(
        `An error occurred, '${error}'. Please try again later.`,
        'Error Creating Video'
      );
    }
  }

  get formArray(): AbstractControl {
    return this.pageForm.get('steps')!;
  }
}
